/**
 * GenericSuggestionCard — Fallback-Renderer für Suggestion-Kinds,
 * für die noch kein spezialisierter Card-Renderer existiert.
 *
 * Versucht, eine `rationale` aus dem Payload anzuzeigen, sonst gibt
 * es nur den Kind-Identifier + Akzeptanz-Buttons. Damit kann eine
 * neue Capability ohne UI-Code-Change zumindest sichtbar werden.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import type { AiSuggestion } from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

export const GenericSuggestionCard: React.FC<Props> = ({ suggestion }) => {
  const { resolve } = useAiCompanion();

  const rationale = extractRationale(suggestion.payload);

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          {suggestion.kind}
        </span>
        <span className="text-xs text-muted-foreground">
          {new Date(suggestion.created_at).toLocaleString('de-DE')}
        </span>
      </header>

      {rationale ? (
        <p className="mb-3 text-sm text-foreground">{rationale}</p>
      ) : (
        <p className="mb-3 text-sm italic text-muted-foreground">
          Kein Inhalt-Renderer für diesen Vorschlagstyp installiert.
        </p>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => void resolve(suggestion, 'accepted')}
        >
          Annehmen
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void resolve(suggestion, 'dismissed')}
        >
          Verwerfen
        </Button>
      </div>
    </article>
  );
};

function extractRationale(payload: unknown): string | null {
  if (payload && typeof payload === 'object' && 'rationale' in payload) {
    const r = (payload as { rationale?: unknown }).rationale;
    if (typeof r === 'string' && r.length > 0) return r;
  }
  return null;
}
